import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Swal from 'sweetalert2';
import authAxios from '../../services/Token';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSearch,
  faFilter,
  faTimes,
  faEye,
  faPrint,
  faTrashAlt,
  faCheck,
  faFileInvoice,
  faClipboardCheck,
  faSync,
  faPlus,
  faCalendarAlt,
  faUserTie,
  faMoneyBillWave,
  faCheckCircle,
  faClock,
  faBan,
  faTags,
  faEdit,
  faFileAlt,
  faExternalLinkAlt
} from '@fortawesome/free-solid-svg-icons';
import OrdenesDetalle from '../ordenes/index_detalle';

const formatMoneda = (val) => {
  return new Intl.NumberFormat('es-CO', {
    style: 'currency',
    currency: 'COP',
    minimumFractionDigits: 0
  }).format(val || 0);
};

const formatFecha = (f) => {
  if (!f) return '-';
  const str = String(f).trim().split(' ')[0];
  const parts = str.split('-');
  if (parts.length === 3) {
    return `${parts[2]}/${parts[1]}/${parts[0]}`;
  }
  return f;
};

const GestionCuentaCobro = () => {
  // Filtros
  const [profesional, setProfesional] = useState('');
  const [cargo, setCargo] = useState('');
  const [estado, setEstado] = useState('todos');
  const [fechaDesde, setFechaDesde] = useState('');
  const [fechaHasta, setFechaHasta] = useState('');
  const [busqueda, setBusqueda] = useState('');

  // Datos
  const [cuentas, setCuentas] = useState([]);
  const [profesionales, setProfesionales] = useState([]);
  const [cargos, setCargos] = useState([]);
  const [cargando, setCargando] = useState(false);

  // Modal Detalle
  const [showModal, setShowModal] = useState(false);
  const [cuentaSeleccionada, setCuentaSeleccionada] = useState(null);
  const [ordenesDetalle, setOrdenesDetalle] = useState([]);
  const [cargandoDetalle, setCargandoDetalle] = useState(false);
  const [valoresAdicionales, setValoresAdicionales] = useState({});
  const [guardandoAdicional, setGuardandoAdicional] = useState({});

  // Modal Desglose Tarifas Diferenciadas
  const [showModalDesglose, setShowModalDesglose] = useState(false);
  const [ordenDesglose, setOrdenDesglose] = useState(null);
  const [tramosDesglose, setTramosDesglose] = useState([]);
  const [guardandoDesglose, setGuardandoDesglose] = useState(false);

  // Modal Ver Orden Interna
  const [showModalOrden, setShowModalOrden] = useState(false);
  const [ordenSeleccionadaId, setOrdenSeleccionadaId] = useState(null);
  const [ordenSeleccionadaPaciente, setOrdenSeleccionadaPaciente] = useState('');

  // Modal Planilla de Seguridad Social
  const [modalPlanilla, setModalPlanilla] = useState(null);

  // Abrir modal para ver Planilla
  const abrirModalPlanilla = (cuenta) => {
    if (!cuenta || !cuenta.planilla_url) {
      Swal.fire('Atención', 'No hay planilla de seguridad social adjunta en esta cuenta.', 'warning');
      return;
    }
    const esPdf = String(cuenta.planilla_url).toLowerCase().includes('.pdf');
    setModalPlanilla({
      cuentaId: cuenta.id,
      url: cuenta.planilla_url,
      esPdf,
      profesional: cuenta.profesional_nombre || ''
    });
  };

  // Quitar / eliminar planilla de la cuenta
  const handleQuitarPlanilla = (idCuenta) => {
    Swal.fire({
      title: '¿Quitar planilla?',
      text: '¿Está seguro de quitar el archivo PDF de la planilla de esta cuenta? La cuenta quedará marcada como "Falta planilla".',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Sí, quitar planilla',
      cancelButtonText: 'Cancelar'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const resp = await authAxios.post('cuenta_cobro/eliminar_planilla', { id_cuenta: idCuenta });
          if (resp.data && resp.data.status) {
            Swal.fire('¡Eliminada!', 'El PDF de la planilla ha sido retirado exitosamente.', 'success');
            setCuentaSeleccionada(prev => prev && prev.id === idCuenta ? { ...prev, planilla: null, planilla_url: null, tiene_planilla: false } : prev);
            setCuentas(prev => prev.map(c => c.id === idCuenta ? { ...c, planilla: null, planilla_url: null, tiene_planilla: false } : c));
            if (modalPlanilla && modalPlanilla.cuentaId === idCuenta) {
              setModalPlanilla(null);
            }
          } else {
            Swal.fire('Error', resp.data?.message || 'No se pudo quitar la planilla', 'error');
          }
        } catch (error) {
          console.error(error);
          Swal.fire('Error', 'Error al intentar quitar la planilla', 'error');
        }
      }
    });
  };

  // Cargar lista con filtros
  const cargarCuentas = useCallback(async () => {
    setCargando(true);
    try {
      const params = {
        profesional,
        cargo,
        estado,
        fecha_desde: fechaDesde,
        fecha_hasta: fechaHasta,
        busqueda
      };
      const { data } = await authAxios.post('cuenta_cobro/gestion', params);
      if (data.status) {
        setCuentas(data.data || []);
        if (data.profesionales) {
          setProfesionales(data.profesionales);
        }
        if (data.cargos) {
          setCargos(data.cargos);
        }
      } else {
        Swal.fire('Error', data.message || 'No se pudieron cargar las cuentas', 'error');
      }
    } catch (err) {
      console.error('Error al cargar cuentas de cobro', err);
      Swal.fire('Error', 'Error de conexión al cargar cuentas de cobro', 'error');
    } finally {
      setCargando(false);
    }
  }, [profesional, cargo, estado, fechaDesde, fechaHasta, busqueda]);

  useEffect(() => {
    cargarCuentas();
  }, [cargarCuentas]);

  const limpiarFiltros = () => {
    setProfesional('');
    setCargo('');
    setEstado('todos');
    setFechaDesde('');
    setFechaHasta('');
    setBusqueda('');
  };

  // Abrir modal de detalle
  const verDetalleCuenta = async (idCuenta) => {
    setShowModal(true);
    setCargandoDetalle(true);
    try {
      const { data } = await authAxios.get(`cuenta_cobro/detalle/${idCuenta}`);
      if (data.status) {
        setCuentaSeleccionada(data.cuenta);
        const ords = data.ordenes || [];
        setOrdenesDetalle(ords);
        // Inicializar inputs de valor adicional
        const vals = {};
        ords.forEach(o => {
          vals[o.id] = o.valor_adicional || 0;
        });
        setValoresAdicionales(vals);
      } else {
        Swal.fire('Error', data.message || 'No se pudo cargar la cuenta', 'error');
        setShowModal(false);
      }
    } catch (err) {
      console.error('Error al obtener detalle', err);
      Swal.fire('Error', 'No se pudo obtener el detalle de la cuenta', 'error');
      setShowModal(false);
    } finally {
      setCargandoDetalle(false);
    }
  };

  const cerrarModal = () => {
    setShowModal(false);
    setCuentaSeleccionada(null);
    setOrdenesDetalle([]);
    setValoresAdicionales({});
  };

  // Guardar Valor Adicional para una orden
  const guardarValorAdicional = async (idOrden) => {
    if (!cuentaSeleccionada) return;
    const val = Number(valoresAdicionales[idOrden] || 0);

    if (val < 0) {
      Swal.fire('Atención', 'El valor adicional no puede ser negativo', 'warning');
      return;
    }

    setGuardandoAdicional(prev => ({ ...prev, [idOrden]: true }));
    try {
      const { data } = await authAxios.post('cuenta_cobro/actualizar_adicional', {
        id_cuenta: cuentaSeleccionada.id,
        id_orden: idOrden,
        valor_adicional: val
      });

      if (data.status) {
        // Actualizar cuenta y orden localmente
        setCuentaSeleccionada(prev => prev ? { ...prev, total: data.total_cuenta } : null);
        setOrdenesDetalle(prev =>
          prev.map(o => (o.id === idOrden ? { ...o, valor_adicional: val, costo: data.costo_orden } : o))
        );
        // Actualizar en la lista general también
        setCuentas(prev =>
          prev.map(c => (c.id === cuentaSeleccionada.id ? { ...c, total: data.total_cuenta } : c))
        );
        Swal.fire({
          icon: 'success',
          title: 'Guardado',
          text: `Valor adicional de ${formatMoneda(val)} aplicado a la orden #${idOrden}`,
          timer: 1800,
          showConfirmButton: false
        });
      } else {
        Swal.fire('Error', data.message || 'No se pudo actualizar el valor adicional', 'error');
      }
    } catch (err) {
      console.error(err);
      Swal.fire('Error', 'Error al actualizar el valor adicional', 'error');
    } finally {
      setGuardandoAdicional(prev => ({ ...prev, [idOrden]: false }));
    }
  };

  // Quitar orden de la cuenta con observación obligatoria
  const quitarOrdenDeCuenta = async (idOrden) => {
    if (!cuentaSeleccionada) return;

    const { value: observacion } = await Swal.fire({
      title: `¿Quitar orden #${idOrden} de la cuenta?`,
      input: 'textarea',
      inputLabel: 'Motivo u Observación (Obligatorio)',
      inputPlaceholder: 'Escriba por qué se devuelve esta orden al profesional...',
      inputAttributes: {
        'aria-label': 'Motivo u observación de la devolución'
      },
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Sí, quitar orden',
      cancelButtonText: 'Cancelar',
      inputValidator: (value) => {
        if (!value || !value.trim()) {
          return '¡Debe ingresar una observación explicando el motivo!';
        }
      }
    });

    if (!observacion) return;

    Swal.fire({
      title: 'Procesando...',
      text: 'Retirando orden y registrando observación',
      allowOutsideClick: false,
      didOpen: () => Swal.showLoading()
    });

    try {
      const { data } = await authAxios.post('cuenta_cobro/quitar_orden', {
        id_cuenta: cuentaSeleccionada.id,
        id_orden: idOrden,
        observacion: observacion.trim()
      });

      if (data.status) {
        // Remover de la tabla modal y actualizar total
        setOrdenesDetalle(prev => prev.filter(o => o.id !== idOrden));
        setCuentaSeleccionada(prev => prev ? { ...prev, total: data.total_cuenta } : null);

        // Actualizar en lista general
        setCuentas(prev =>
          prev.map(c =>
            c.id === cuentaSeleccionada.id
              ? { ...c, total: data.total_cuenta, cantidad_ordenes: data.ordenes_restantes }
              : c
          )
        );

        Swal.fire('Éxito', data.message || 'Orden retirada correctamente de la cuenta', 'success');
      } else {
        Swal.fire('Error', data.message || 'No se pudo retirar la orden', 'error');
      }
    } catch (err) {
      console.error(err);
      Swal.fire('Error', 'Error al retirar la orden de la cuenta', 'error');
    }
  };

  // Aprobar y Liquidar Cuenta
  const aprobarCuenta = (idCuenta) => {
    Swal.fire({
      title: '¿Aprobar y Liquidar Cuenta de Cobro?',
      html: `Esta acción generará las liquidaciones correspondientes para cada atención teniendo en cuenta los <b>valores adicionales</b> registrados y cambiará el estado de la cuenta a <b>APROBADO</b>.`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#198754',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Sí, aprobar y liquidar',
      cancelButtonText: 'Cancelar',
      reverseButtons: true
    }).then(async (result) => {
      if (result.isConfirmed) {
        Swal.fire({
          title: 'Aprobando y Liquidando...',
          text: 'Generando liquidaciones, por favor espere',
          allowOutsideClick: false,
          didOpen: () => Swal.showLoading()
        });

        try {
          const { data } = await authAxios.post('cuenta_cobro/aprobar_y_liquidar', { id: idCuenta });
          if (data.status) {
            Swal.fire('¡Aprobada!', data.message || 'Cuenta de cobro aprobada y liquidada exitosamente.', 'success');
            // Refrescar modal y lista general
            verDetalleCuenta(idCuenta);
            cargarCuentas();
          } else {
            Swal.fire('Error', data.message || 'No se pudo aprobar la cuenta de cobro', 'error');
          }
        } catch (err) {
          console.error(err);
          Swal.fire('Error', 'Ocurrió un error al aprobar y liquidar la cuenta', 'error');
        }
      }
    });
  };

  // Imprimir PDF (Descarga / Visualización en ventana)
  const imprimirPdf = async (idCuenta) => {
    try {
      Swal.fire({
        title: 'Generando PDF...',
        text: 'Por favor espere un momento',
        allowOutsideClick: false,
        didOpen: () => Swal.showLoading()
      });

      const { data } = await authAxios.get(`cuenta_cobro/imprimir_base64/${idCuenta}`);
      Swal.close();

      if (data.status && data.data && data.data.pdf_base64) {
        const base64 = data.data.pdf_base64;
        const byteCharacters = atob(base64);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'application/pdf' });
        const fileURL = URL.createObjectURL(blob);
        window.open(fileURL, '_blank');
      } else {
        Swal.fire('Error', data.message || 'No se pudo generar el documento PDF', 'error');
      }
    } catch (err) {
      console.error(err);
      Swal.close();
      Swal.fire('Error', 'Error al generar o visualizar el PDF', 'error');
    }
  };

    // Ver orden médica interna completa en modal
  const abrirModalOrden = async (idOrden, paciente) => {
    try {
      Swal.fire({
        title: 'Cargando orden...',
        text: 'Consultando información de la orden médica',
        allowOutsideClick: false,
        didOpen: () => Swal.showLoading()
      });

      let archivoId = 200;
      try {
        const resp = await authAxios.get(`ordenes/archivo_de_orden/${idOrden}`);
        if (resp.data && resp.data.status && resp.data.archivo) {
          archivoId = resp.data.archivo;
        }
      } catch (err) {
        console.warn("No se pudo obtener archivo específico, usando 200:", err);
      }

      window.localStorage.setItem('Archivo', '200');
      window.localStorage.setItem('TablaId', String(archivoId));
      window.localStorage.setItem('OrdenId', String(idOrden));
      window.localStorage.setItem('orden_interna_id', String(idOrden));

      setOrdenSeleccionadaId(idOrden);
      setOrdenSeleccionadaPaciente(paciente || '');
      Swal.close();
      setShowModalOrden(true);
    } catch (e) {
      console.error("Error abriendo modal de orden:", e);
      Swal.close();
      Swal.fire('Error', 'No se pudo cargar la orden médica seleccionada', 'error');
    }
  };

  const cerrarModalOrden = () => {
    setShowModalOrden(false);
    setOrdenSeleccionadaId(null);
    setOrdenSeleccionadaPaciente('');
    window.localStorage.removeItem('OrdenId');
  };

  // ── Funciones de Desglose de Tarifas Diferenciadas ──
  const abrirModalDesglose = (ord) => {
    setOrdenDesglose(ord);
    let tramos = [];
    let desg = ord.desglose_tarifas;
    if (typeof desg === 'string') {
      try { desg = JSON.parse(desg); } catch(e) { desg = null; }
    }
    if (desg && Array.isArray(desg) && desg.length > 0) {
      tramos = desg.map(t => ({
        cantidad: Number(t.cantidad || 0),
        precio: Number(t.precio || 0),
        subtotal: Number(t.cantidad || 0) * Number(t.precio || 0)
      }));
    } else {
      const cant = Number(ord.cantidad || 1);
      const pr = Number(ord.costo_unitario || 0);
      tramos = [{ cantidad: cant, precio: pr, subtotal: cant * pr }];
    }
    setTramosDesglose(tramos);
    setShowModalDesglose(true);
  };

  const cerrarModalDesglose = () => {
    setShowModalDesglose(false);
    setOrdenDesglose(null);
    setTramosDesglose([]);
  };

  const agregarTramo = () => {
    const prBase = Number(ordenDesglose?.costo_unitario || 0);
    setTramosDesglose(prev => [
      ...prev,
      { cantidad: 1, precio: prBase, subtotal: prBase }
    ]);
  };

  const eliminarTramo = (idx) => {
    if (tramosDesglose.length <= 1) {
      Swal.fire('Atención', 'Debe haber al menos un tramo de tarifa', 'warning');
      return;
    }
    setTramosDesglose(prev => prev.filter((_, i) => i !== idx));
  };

  const cambiarTramo = (idx, campo, valor) => {
    const valNum = Number(valor);
    setTramosDesglose(prev =>
      prev.map((t, i) => {
        if (i === idx) {
          const updated = { ...t, [campo]: valNum };
          updated.subtotal = Number(updated.cantidad || 0) * Number(updated.precio || 0);
          return updated;
        }
        return t;
      })
    );
  };

  const guardarDesglose = async () => {
    if (!cuentaSeleccionada || !ordenDesglose) return;

    const totalCantEsperada = Number(ordenDesglose.cantidad || 1);
    const cantActual = tramosDesglose.reduce((acc, curr) => acc + Number(curr.cantidad || 0), 0);

    if (cantActual !== totalCantEsperada) {
      Swal.fire(
        'Validación de Cantidad',
        `La suma de cantidades en los tramos (${cantActual}) debe ser exactamente igual al total de atenciones de la orden (${totalCantEsperada}).`,
        'warning'
      );
      return;
    }

    for (let i = 0; i < tramosDesglose.length; i++) {
      if (tramosDesglose[i].cantidad <= 0) {
        Swal.fire('Atención', `La cantidad del tramo #${i + 1} debe ser mayor a 0`, 'warning');
        return;
      }
      if (tramosDesglose[i].precio < 0) {
        Swal.fire('Atención', `El precio del tramo #${i + 1} no puede ser negativo`, 'warning');
        return;
      }
    }

    setGuardandoDesglose(true);
    try {
      const { data } = await authAxios.post('cuenta_cobro/actualizar_desglose', {
        id_cuenta: cuentaSeleccionada.id,
        id_orden: ordenDesglose.id,
        desglose_tarifas: tramosDesglose
      });

      if (data.status) {
        setCuentaSeleccionada(prev => prev ? { ...prev, total: data.total_cuenta } : null);
        setOrdenesDetalle(prev =>
          prev.map(o => (o.id === ordenDesglose.id ? {
            ...o,
            costo_atencion: data.costo_atencion,
            costo: data.costo_orden,
            desglose_tarifas: data.desglose_tarifas
          } : o))
        );
        setCuentas(prev =>
          prev.map(c => (c.id === cuentaSeleccionada.id ? { ...c, total: data.total_cuenta } : c))
        );

        cerrarModalDesglose();
        Swal.fire({
          icon: 'success',
          title: 'Tarifas Actualizadas',
          text: `Se aplicó el desglose de tarifas a la orden #${ordenDesglose.id}`,
          timer: 1800,
          showConfirmButton: false
        });
      } else {
        Swal.fire('Error', data.message || 'No se pudo guardar el desglose', 'error');
      }
    } catch (err) {
      console.error(err);
      Swal.fire('Error', 'Error al guardar el desglose de tarifas', 'error');
    } finally {
      setGuardandoDesglose(false);
    }
  };

  // Totales de resumen
  const metricas = useMemo(() => {
    let totIniciadas = 0;
    let totAprobadas = 0;
    let sumaTotal = 0;
    cuentas.forEach(c => {
      if (c.estado === 1) totIniciadas++;
      if (c.estado === 4) totAprobadas++;
      sumaTotal += Number(c.total || 0);
    });
    return {
      totalCuentas: cuentas.length,
      iniciadas: totIniciadas,
      aprobadas: totAprobadas,
      sumaTotal
    };
  }, [cuentas]);

  return (
    <div className="container-fluid py-4" style={{ backgroundColor: '#F5F7FC', minHeight: '100vh' }}>
      {/* ══ HEADER ══ */}
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <div>
          <h3 className="fw-bold mb-1" style={{ color: '#1A2340' }}>
            <FontAwesomeIcon icon={faFileInvoice} className="text-primary me-2" />
            Gestión Cuentas de Cobro
          </h3>
          <p className="text-muted mb-0" style={{ fontSize: '13px' }}>
            Auditoría, adición de valores, liquidación y aprobación de cuentas por profesional
          </p>
        </div>
        <button className="btn btn-outline-primary btn-sm d-flex align-items-center gap-1" onClick={cargarCuentas} disabled={cargando}>
          <FontAwesomeIcon icon={faSync} className={cargando ? 'fa-spin' : ''} />
          <span>Actualizar</span>
        </button>
      </div>

      {/* ══ TARJETAS DE MÉTRICAS RÁPIDAS ══ */}
      <div className="row g-3 mb-3">
        <div className="col-12 col-sm-6 col-md-3">
          <div className="card shadow-sm border-0 rounded-3 p-3 bg-white">
            <div className="d-flex align-items-center justify-content-between">
              <div>
                <span className="text-muted" style={{ fontSize: '11px', textTransform: 'uppercase', fontWeight: 600 }}>Total Cuentas</span>
                <h4 className="fw-bold mb-0 mt-1" style={{ color: '#1A2340' }}>{metricas.totalCuentas}</h4>
              </div>
              <div className="rounded-circle p-2 bg-light text-primary">
                <FontAwesomeIcon icon={faFileInvoice} style={{ fontSize: '20px' }} />
              </div>
            </div>
          </div>
        </div>
        <div className="col-12 col-sm-6 col-md-3">
          <div className="card shadow-sm border-0 rounded-3 p-3 bg-white">
            <div className="d-flex align-items-center justify-content-between">
              <div>
                <span className="text-muted" style={{ fontSize: '11px', textTransform: 'uppercase', fontWeight: 600 }}>Iniciadas</span>
                <h4 className="fw-bold mb-0 mt-1 text-primary">{metricas.iniciadas}</h4>
              </div>
              <div className="rounded-circle p-2 bg-primary bg-opacity-10 text-primary">
                <FontAwesomeIcon icon={faClock} style={{ fontSize: '20px' }} />
              </div>
            </div>
          </div>
        </div>
        <div className="col-12 col-sm-6 col-md-3">
          <div className="card shadow-sm border-0 rounded-3 p-3 bg-white">
            <div className="d-flex align-items-center justify-content-between">
              <div>
                <span className="text-muted" style={{ fontSize: '11px', textTransform: 'uppercase', fontWeight: 600 }}>Aprobadas</span>
                <h4 className="fw-bold mb-0 mt-1 text-success">{metricas.aprobadas}</h4>
              </div>
              <div className="rounded-circle p-2 bg-success bg-opacity-10 text-success">
                <FontAwesomeIcon icon={faCheckCircle} style={{ fontSize: '20px' }} />
              </div>
            </div>
          </div>
        </div>
        <div className="col-12 col-sm-6 col-md-3">
          <div className="card shadow-sm border-0 rounded-3 p-3 bg-white">
            <div className="d-flex align-items-center justify-content-between">
              <div>
                <span className="text-muted" style={{ fontSize: '11px', textTransform: 'uppercase', fontWeight: 600 }}>Valor Total Cuentas</span>
                <h5 className="fw-bold mb-0 mt-1 text-success">{formatMoneda(metricas.sumaTotal)}</h5>
              </div>
              <div className="rounded-circle p-2 bg-success bg-opacity-10 text-success">
                <FontAwesomeIcon icon={faMoneyBillWave} style={{ fontSize: '20px' }} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ══ TARJETA DE FILTROS ══ */}
      <div className="card shadow-sm border-0 rounded-3 mb-4 bg-white">
        <div className="card-body p-3">
          <div className="row g-2 align-items-end">
            {/* Filtro Profesional */}
            <div className="col-12 col-md-3 col-xl-2">
              <label className="form-label fw-bold" style={{ fontSize: '12px', color: '#1A2340' }}>
                <FontAwesomeIcon icon={faUserTie} className="me-1 text-muted" />
                Profesional
              </label>
              <select
                className="form-select form-select-sm"
                value={profesional}
                onChange={e => setProfesional(e.target.value)}
                style={{ fontSize: '12px' }}
              >
                <option value="">-- Todos los profesionales --</option>
                {profesionales.map(p => (
                  <option key={p.id} value={p.id}>
                    {p.nombre_completo} {p.cedula ? `(C.C. ${p.cedula})` : ''}
                  </option>
                ))}
              </select>
            </div>

            {/* Filtro Cargo */}
            <div className="col-12 col-md-2 col-xl-2">
              <label className="form-label fw-bold" style={{ fontSize: '12px', color: '#1A2340' }}>
                <FontAwesomeIcon icon={faTags} className="me-1 text-muted" />
                Cargo
              </label>
              <select
                className="form-select form-select-sm"
                value={cargo}
                onChange={e => setCargo(e.target.value)}
                style={{ fontSize: '12px' }}
              >
                <option value="">-- Todos los cargos --</option>
                {cargos.map((cg, idx) => (
                  <option key={idx} value={cg}>
                    {cg}
                  </option>
                ))}
              </select>
            </div>

            {/* Filtro Estado */}
            <div className="col-12 col-md-2 col-xl-2">
              <label className="form-label fw-bold" style={{ fontSize: '12px', color: '#1A2340' }}>
                <FontAwesomeIcon icon={faFilter} className="me-1 text-muted" />
                Estado
              </label>
              <select
                className="form-select form-select-sm"
                value={estado}
                onChange={e => setEstado(e.target.value)}
                style={{ fontSize: '12px' }}
              >
                <option value="todos">Todos los estados</option>
                <option value="1">1 - INICIADA</option>
                <option value="2">2 - EN PROCESO</option>
                <option value="3">3 - CANCELADO</option>
                <option value="4">4 - APROBADO</option>
              </select>
            </div>

            {/* Fecha Desde */}
            <div className="col-12 col-md-2 col-xl-2">
              <label className="form-label fw-bold" style={{ fontSize: '12px', color: '#1A2340' }}>
                <FontAwesomeIcon icon={faCalendarAlt} className="me-1 text-muted" />
                Fecha Desde
              </label>
              <input
                type="date"
                className="form-control form-control-sm"
                value={fechaDesde}
                onChange={e => setFechaDesde(e.target.value)}
                style={{ fontSize: '12px' }}
              />
            </div>

            {/* Fecha Hasta */}
            <div className="col-12 col-md-2 col-xl-2">
              <label className="form-label fw-bold" style={{ fontSize: '12px', color: '#1A2340' }}>
                <FontAwesomeIcon icon={faCalendarAlt} className="me-1 text-muted" />
                Fecha Hasta
              </label>
              <input
                type="date"
                className="form-control form-control-sm"
                value={fechaHasta}
                onChange={e => setFechaHasta(e.target.value)}
                style={{ fontSize: '12px' }}
              />
            </div>

            {/* Buscador */}
            <div className="col-12 col-md-2 col-xl-1">
              <label className="form-label fw-bold" style={{ fontSize: '12px', color: '#1A2340' }}>
                <FontAwesomeIcon icon={faSearch} className="me-1 text-muted" />
                Búsqueda
              </label>
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="# Cuenta o cédula..."
                value={busqueda}
                onChange={e => setBusqueda(e.target.value)}
                style={{ fontSize: '12px' }}
              />
            </div>

            {/* Botón Limpiar */}
            <div className="col-12 col-md-1 col-xl-1 d-flex gap-1">
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm w-100"
                onClick={limpiarFiltros}
                title="Limpiar filtros"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ══ TABLA PRINCIPAL DE CUENTAS DE COBRO ══ */}
      <div className="card shadow-sm border-0 rounded-3 bg-white">
        <div className="card-body p-0">
          {cargando ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Cargando...</span>
              </div>
              <p className="mt-2 text-muted" style={{ fontSize: '13px' }}>Cargando cuentas de cobro...</p>
            </div>
          ) : cuentas.length === 0 ? (
            <div className="text-center py-5 text-muted">
              <FontAwesomeIcon icon={faFileInvoice} style={{ fontSize: '40px', opacity: 0.3 }} />
              <p className="mt-2 mb-0" style={{ fontSize: '14px' }}>No se encontraron cuentas de cobro con los filtros aplicados.</p>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0" style={{ fontSize: '13px' }}>
                <thead style={{ backgroundColor: '#1A2340', color: '#ffffff' }}>
                  <tr>
                    <th style={{ width: '80px', textAlign: 'center', padding: '12px 10px' }}># ID</th>
                    <th style={{ padding: '12px 10px' }}>PROFESIONAL</th>
                    <th style={{ padding: '12px 10px', minWidth: '130px' }}>CARGO</th>
                    <th style={{ padding: '12px 10px' }}>PERIODO ATENCIÓN</th>
                    <th style={{ width: '90px', textAlign: 'center', padding: '12px 10px' }}>ÓRDENES</th>
                    <th style={{ width: '130px', textAlign: 'center', padding: '12px 10px' }}>PLANILLA</th>
                    <th style={{ width: '130px', textAlign: 'right', padding: '12px 10px' }}>TOTAL CUENTA</th>
                    <th style={{ width: '120px', textAlign: 'center', padding: '12px 10px' }}>ESTADO</th>
                    <th style={{ width: '110px', textAlign: 'center', padding: '12px 10px' }}>F. REGISTRO</th>
                    <th style={{ width: '140px', textAlign: 'center', padding: '12px 10px' }}>ACCIONES</th>
                  </tr>
                </thead>
                <tbody>
                  {cuentas.map(c => (
                    <tr key={c.id}>
                      <td style={{ textAlign: 'center', fontWeight: 'bold', color: '#0054A1' }}>
                        #{c.id}
                      </td>
                      <td>
                        <div className="fw-bold" style={{ color: '#1A2340' }}>{c.profesional_nombre || 'N/A'}</div>
                        {c.profesional_cedula && (
                          <div className="text-muted" style={{ fontSize: '11px' }}>C.C. {c.profesional_cedula}</div>
                        )}
                      </td>
                      <td>
                        <span
                          className="badge bg-secondary-subtle text-secondary border border-secondary-subtle px-2 py-1 text-wrap text-start fw-bold"
                          style={{ fontSize: '11px', letterSpacing: '0.2px', borderRadius: '6px' }}
                        >
                          {c.profesional_cargo || '-'}
                        </span>
                      </td>
                      <td>
                        <div style={{ fontSize: '12px', fontWeight: 600, color: '#374151' }}>
                          {formatFecha(c.periodo_inicial)} al {formatFecha(c.periodo_final)}
                        </div>
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <span className="badge bg-light text-primary border" style={{ fontSize: '11px', fontWeight: 700 }}>
                          {c.cantidad_ordenes} orden(es)
                        </span>
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        {c.planilla ? (
                          <div className="d-flex justify-content-center align-items-center gap-1">
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-success fw-bold d-inline-flex align-items-center gap-1 shadow-sm"
                              style={{ fontSize: '11px', padding: '4px 9px', borderRadius: '8px' }}
                              onClick={() => abrirModalPlanilla(c)}
                              title="Ver planilla de seguridad social"
                            >
                              <FontAwesomeIcon icon={faFileAlt} />
                              <span>Ver Planilla</span>
                            </button>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger shadow-sm"
                              style={{ fontSize: '11px', padding: '4px 8px', borderRadius: '8px' }}
                              onClick={() => handleQuitarPlanilla(c.id)}
                              title="Quitar PDF de la planilla"
                            >
                              <FontAwesomeIcon icon={faTrashAlt} />
                            </button>
                          </div>
                        ) : (
                          <span
                            className="badge bg-danger-subtle text-danger border border-danger-subtle d-inline-flex align-items-center gap-2 py-1 px-2"
                            style={{ fontSize: '11px', fontWeight: 700, borderRadius: '8px' }}
                            title="El profesional aún no ha subido la planilla"
                          >
                            <span className="led-blinking-red"></span>
                            <span>Falta planilla</span>
                          </span>
                        )}
                      </td>
                      <td style={{ textAlign: 'right', fontWeight: 800, color: '#198754', fontSize: '14px' }}>
                        {formatMoneda(c.total)}
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <span className={`badge ${
                          c.estado === 1 ? 'bg-primary' :
                          c.estado === 2 ? 'bg-warning text-dark' :
                          c.estado === 3 ? 'bg-danger' :
                          c.estado === 4 ? 'bg-success' : 'bg-secondary'
                        }`} style={{ padding: '6px 12px', borderRadius: '20px', fontSize: '10px', textTransform: 'uppercase' }}>
                          {c.estado_nombre}
                        </span>
                      </td>
                      <td style={{ textAlign: 'center', fontSize: '12px', color: '#6b7280' }}>
                        {formatFecha(c.fecha_registro)}
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <div className="d-flex justify-content-center gap-1">
                          <button
                            type="button"
                            className="btn btn-primary btn-sm"
                            onClick={() => verDetalleCuenta(c.id)}
                            title="Ver detalle y gestionar cuenta"
                          >
                            <FontAwesomeIcon icon={faEye} />
                          </button>
                          <button
                            type="button"
                            className="btn btn-outline-secondary btn-sm"
                            onClick={() => imprimirPdf(c.id)}
                            title="Imprimir PDF"
                          >
                            <FontAwesomeIcon icon={faPrint} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════════ */}
      {/* MODAL DETALLE DE ÓRDENES, ADICIONAL Y GESTIÓN DE CUENTA   */}
      {/* ══════════════════════════════════════════════════════════ */}
      {showModal && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.65)', zIndex: 1060 }} tabIndex="-1">
          <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content" style={{ borderRadius: '16px', overflow: 'hidden', border: 'none', boxShadow: '0 12px 36px rgba(0,0,0,0.25)' }}>
              
              {/* Modal Header */}
              <div className="modal-header d-flex justify-content-between align-items-center" style={{ borderBottom: '1px solid #ECEFF5', padding: '16px 20px', background: '#ffffff' }}>
                <div className="d-flex align-items-center gap-2">
                  <h5 className="modal-title m-0" style={{ fontWeight: 800, color: '#1A2340', fontSize: '18px' }}>
                    Órdenes Cuenta #{cuentaSeleccionada?.id}
                  </h5>
                  {cuentaSeleccionada && (
                    <span className={`badge ${
                      cuentaSeleccionada.estado === 1 ? 'bg-primary' :
                      cuentaSeleccionada.estado === 2 ? 'bg-warning text-dark' :
                      cuentaSeleccionada.estado === 3 ? 'bg-danger' :
                      cuentaSeleccionada.estado === 4 ? 'bg-success' : 'bg-secondary'
                    }`} style={{ fontSize: '11px', textTransform: 'uppercase', padding: '6px 12px', borderRadius: '20px' }}>
                      {cuentaSeleccionada.estado_nombre}
                    </span>
                  )}
                </div>
                <button type="button" className="btn-close" onClick={cerrarModal}></button>
              </div>

              {/* Modal Subheader Summary */}
              {cuentaSeleccionada && (
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2" style={{ backgroundColor: '#F8F9FD', padding: '14px 20px', borderBottom: '1px solid #ECEFF5' }}>
                  <div>
                    <span style={{ fontSize: '11px', color: '#8C97AC', display: 'block', textTransform: 'uppercase', fontWeight: 600 }}>Profesional</span>
                    <span style={{ fontSize: '13px', fontWeight: 700, color: '#1A2340' }}>
                      {cuentaSeleccionada.profesional_nombre || 'N/A'} {cuentaSeleccionada.profesional_cedula ? `— C.C. ${cuentaSeleccionada.profesional_cedula}` : ''}
                    </span>
                  </div>
                  <div>
                    <span style={{ fontSize: '11px', color: '#8C97AC', display: 'block', textTransform: 'uppercase', fontWeight: 600 }}>Periodo</span>
                    <span style={{ fontSize: '13px', fontWeight: 700, color: '#1A2340' }}>
                      {formatFecha(cuentaSeleccionada.periodo_inicial)} al {formatFecha(cuentaSeleccionada.periodo_final)}
                    </span>
                  </div>
                  <div>
                    <span style={{ fontSize: '11px', color: '#8C97AC', display: 'block', textTransform: 'uppercase', fontWeight: 600 }}>Planilla SS</span>
                    {cuentaSeleccionada.planilla ? (
                      <div className="d-flex align-items-center gap-1 mt-1">
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-success fw-bold d-inline-flex align-items-center gap-1 shadow-sm"
                          style={{ fontSize: '11px', padding: '3px 8px', borderRadius: '7px' }}
                          onClick={() => abrirModalPlanilla(cuentaSeleccionada)}
                          title="Ver planilla de seguridad social"
                        >
                          <FontAwesomeIcon icon={faFileAlt} />
                          <span>Ver Planilla Subida</span>
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger fw-bold d-inline-flex align-items-center gap-1 shadow-sm"
                          style={{ fontSize: '11px', padding: '3px 8px', borderRadius: '7px' }}
                          onClick={() => handleQuitarPlanilla(cuentaSeleccionada.id)}
                          title="Quitar PDF de la planilla"
                        >
                          <FontAwesomeIcon icon={faTrashAlt} />
                          <span>Quitar Planilla</span>
                        </button>
                      </div>
                    ) : (
                      <span
                        className="badge bg-danger-subtle text-danger border border-danger-subtle d-inline-flex align-items-center gap-2 py-1 px-2 mt-1"
                        style={{ fontSize: '11px', fontWeight: 700, borderRadius: '7px' }}
                      >
                        <span className="led-blinking-red"></span>
                        <span>Falta planilla</span>
                      </span>
                    )}
                  </div>
                  <div className="text-end">
                    <span style={{ fontSize: '11px', color: '#8C97AC', display: 'block', textTransform: 'uppercase', fontWeight: 600 }}>Total Cuenta</span>
                    <span style={{ fontSize: '20px', fontWeight: 800, color: '#198754' }}>
                      {formatMoneda(cuentaSeleccionada.total)}
                    </span>
                  </div>
                </div>
              )}

              {/* Modal Body: Tabla de órdenes con edición de valor adicional y opción de quitar */}
              <div className="modal-body p-3" style={{ background: '#ffffff' }}>
                {cargandoDetalle ? (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Cargando...</span>
                    </div>
                    <p className="mt-2 text-muted" style={{ fontSize: '13px' }}>Cargando órdenes de la cuenta...</p>
                  </div>
                ) : ordenesDetalle.length === 0 ? (
                  <div className="text-center py-5 text-muted" style={{ fontSize: '13px' }}>
                    Esta cuenta de cobro no tiene órdenes asociadas.
                  </div>
                ) : (
                  <div className="table-responsive rounded" style={{ border: '1px solid #ECEFF5' }}>
                    <table className="table table-sm align-middle mb-0" style={{ fontSize: '12px' }}>
                      <thead style={{ backgroundColor: '#1A2340', color: '#ffffff' }}>
                        <tr>
                          <th style={{ width: '55px', textAlign: 'center', padding: '10px 6px' }}>ID</th>
                          <th style={{ padding: '10px 8px' }}>PACIENTE</th>
                          <th style={{ width: '50px', textAlign: 'center', padding: '10px 6px' }}>CANT.</th>
                          <th style={{ width: '90px', textAlign: 'center', padding: '10px 6px' }}>F. INICIAL</th>
                          <th style={{ width: '90px', textAlign: 'center', padding: '10px 6px' }}>F. FINAL</th>
                          <th style={{ width: '100px', textAlign: 'right', padding: '10px 8px' }}>COSTO ATENC.</th>
                          <th style={{ width: '150px', textAlign: 'center', padding: '10px 8px' }}>VALOR ADICIONAL</th>
                          <th style={{ width: '110px', textAlign: 'right', padding: '10px 8px' }}>SUBTOTAL</th>
                          {cuentaSeleccionada?.estado !== 4 && (
                            <th style={{ width: '70px', textAlign: 'center', padding: '10px 6px' }}>QUITAR</th>
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        {ordenesDetalle.map(ord => (
                          <tr key={ord.id} style={{ borderBottom: '1px solid #F0F2F6' }}>
                            <td style={{ textAlign: 'center' }}>
                              <a
                                href="#ver-orden"
                                onClick={(e) => {
                                  e.preventDefault();
                                  abrirModalOrden(ord.id, ord.paciente);
                                }}
                                style={{
                                  fontWeight: 700,
                                  color: '#0054A1',
                                  cursor: 'pointer',
                                  textDecoration: 'underline'
                                }}
                                title="Ver orden médica"
                              >
                                #{ord.id}
                              </a>
                            </td>
                            <td>
                              <div style={{ fontWeight: 600, color: '#1A2340' }}>{ord.paciente || '-'}</div>
                              <div style={{ fontSize: '10px', color: '#8C97AC' }}>
                                {ord.paciente_cedula && <span>C.C. {ord.paciente_cedula} &bull; </span>}
                                {ord.atencion && <span>Cod: {ord.atencion}</span>}
                              </div>
                            </td>
                            <td style={{ textAlign: 'center', fontWeight: 600 }}>
                              {ord.cantidad || 1}
                            </td>
                            <td style={{ textAlign: 'center', fontSize: '11px', color: '#5A6478' }}>
                              {formatFecha(ord.fecha_inicial)}
                            </td>
                            <td style={{ textAlign: 'center', fontSize: '11px', color: '#5A6478' }}>
                              {formatFecha(ord.fecha_final)}
                            </td>
                            <td style={{ textAlign: 'right', color: '#5A6478', fontWeight: 600 }}>
                              <div style={{ fontSize: '13px', fontWeight: 700, color: '#1A2340' }}>{formatMoneda(ord.costo_atencion)}</div>
                              {(() => {
                                let tramos = ord.desglose_tarifas;
                                if (typeof tramos === 'string') {
                                  try { tramos = JSON.parse(tramos); } catch(e) { tramos = null; }
                                }
                                if (Array.isArray(tramos) && tramos.length > 1) {
                                  return (
                                    <div style={{ marginTop: '3px' }}>
                                      {tramos.map((t, idx) => (
                                        <div key={idx} style={{ fontSize: '10px', color: '#0054A1', fontWeight: 600 }}>
                                          {t.cantidad} a {formatMoneda(t.precio)}
                                        </div>
                                      ))}
                                    </div>
                                  );
                                }
                                return (
                                  <div style={{ fontSize: '10px', color: '#8C97AC' }}>
                                    {ord.cantidad || 1} x {formatMoneda(ord.costo_unitario)}
                                  </div>
                                );
                              })()}

                              {cuentaSeleccionada?.estado !== 4 && (
                                <button
                                  type="button"
                                  className="btn btn-link btn-xs p-0 text-primary mt-1 d-inline-block"
                                  onClick={() => abrirModalDesglose(ord)}
                                  style={{ fontSize: '11px', textDecoration: 'none' }}
                                  title="Ajustar o desglosar precios para esta orden"
                                >
                                  <FontAwesomeIcon icon={faTags} className="me-1" />
                                  {ord.desglose_tarifas && ord.desglose_tarifas.length > 1 ? 'Editar tarifas' : 'Dividir tarifas'}
                                </button>
                              )}
                            </td>

                            {/* Columna Valor Adicional Editable */}
                            <td style={{ textAlign: 'center' }}>
                              {cuentaSeleccionada?.estado !== 4 ? (
                                <div className="d-flex align-items-center justify-content-center gap-1">
                                  <input
                                    type="number"
                                    min="0"
                                    step="500"
                                    className="form-control form-control-sm text-end"
                                    style={{ width: '90px', fontSize: '11px', padding: '3px 6px' }}
                                    value={valoresAdicionales[ord.id] ?? ord.valor_adicional ?? 0}
                                    onChange={e => {
                                      const v = e.target.value;
                                      setValoresAdicionales(prev => ({ ...prev, [ord.id]: v }));
                                    }}
                                    onKeyDown={e => {
                                      if (e.key === 'Enter') {
                                        guardarValorAdicional(ord.id);
                                      }
                                    }}
                                    title="Ej: Transporte, horas extra u otro adicional"
                                  />
                                  <button
                                    type="button"
                                    className="btn btn-outline-success btn-xs"
                                    onClick={() => guardarValorAdicional(ord.id)}
                                    disabled={guardandoAdicional[ord.id]}
                                    title="Guardar valor adicional"
                                  >
                                    <FontAwesomeIcon icon={faCheck} />
                                  </button>
                                </div>
                              ) : (
                                <span style={{ color: Number(ord.valor_adicional) > 0 ? '#b45309' : '#6b7280', fontWeight: 600 }}>
                                  {formatMoneda(ord.valor_adicional)}
                                </span>
                              )}
                            </td>

                            {/* Subtotal de la orden (Costo atención + Valor adicional) */}
                            <td style={{ textAlign: 'right', fontWeight: 700, color: '#198754' }}>
                              {formatMoneda(ord.costo)}
                            </td>

                            {/* Botón Quitar Orden de la Cuenta */}
                            {cuentaSeleccionada?.estado !== 4 && (
                              <td style={{ textAlign: 'center' }}>
                                <button
                                  type="button"
                                  className="btn btn-outline-danger btn-xs"
                                  onClick={() => quitarOrdenDeCuenta(ord.id)}
                                  title="Quitar orden de la cuenta y registrar motivo de devolución"
                                >
                                  <FontAwesomeIcon icon={faTrashAlt} />
                                </button>
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                      <tfoot style={{ backgroundColor: '#F8F9FD', borderTop: '2px solid #0054A1' }}>
                        <tr>
                          <td colSpan={cuentaSeleccionada?.estado !== 4 ? 7 : 6} className="text-end fw-bold" style={{ padding: '10px 8px', color: '#1A2340' }}>
                            TOTAL CUENTA:
                          </td>
                          <td className="text-end" style={{ color: '#198754', fontSize: '15px', fontWeight: 800, padding: '10px 8px' }}>
                            {formatMoneda(
                              ordenesDetalle.reduce((acc, curr) => acc + Number(curr.costo || 0), 0)
                            )}
                          </td>
                          {cuentaSeleccionada?.estado !== 4 && <td></td>}
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                )}
              </div>

              {/* Modal Footer */}
              <div className="modal-footer d-flex justify-content-between flex-wrap gap-2" style={{ padding: '12px 20px', borderTop: '1px solid #ECEFF5' }}>
                <div className="d-flex gap-2">
                  <button type="button" className="btn btn-secondary btn-sm" onClick={cerrarModal}>
                    Cerrar
                  </button>
                  {cuentaSeleccionada && (
                    <button
                      type="button"
                      className="btn btn-outline-primary btn-sm d-flex align-items-center gap-1"
                      onClick={() => imprimirPdf(cuentaSeleccionada.id)}
                    >
                      <FontAwesomeIcon icon={faPrint} />
                      <span>Imprimir PDF</span>
                    </button>
                  )}
                </div>

                <div>
                  {cuentaSeleccionada && cuentaSeleccionada.estado !== 4 ? (
                    <button
                      type="button"
                      className="btn btn-success btn-sm d-flex align-items-center gap-1"
                      onClick={() => aprobarCuenta(cuentaSeleccionada.id)}
                    >
                      <FontAwesomeIcon icon={faClipboardCheck} />
                      <span>Aprobar Cuenta de Cobro</span>
                    </button>
                  ) : cuentaSeleccionada && cuentaSeleccionada.estado === 4 ? (
                    <span className="badge bg-success p-2 d-flex align-items-center gap-1" style={{ fontSize: '12px' }}>
                      <FontAwesomeIcon icon={faClipboardCheck} />
                      <span>Cuenta Aprobada y Liquidada</span>
                    </span>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════ */}
      {/* MODAL DESGLOSE DE TARIFAS DIFERENCIADAS (ej. 8 a 18k, 2 a 15k) */}
      {/* ══════════════════════════════════════════════════════════ */}
      {showModalDesglose && ordenDesglose && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.65)', zIndex: 1075 }} tabIndex="-1">
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content" style={{ borderRadius: '16px', border: 'none', boxShadow: '0 12px 40px rgba(0,0,0,0.3)', overflow: 'hidden' }}>
              <div className="modal-header" style={{ borderBottom: '1px solid #ECEFF5', padding: '18px 24px', background: '#F8F9FD' }}>
                <div>
                  <h5 className="modal-title fw-bold m-0" style={{ color: '#1A2340', fontSize: '18px' }}>
                    <FontAwesomeIcon icon={faTags} className="text-primary me-2" />
                    Desglose de Tarifas — Orden #{ordenDesglose.id}
                  </h5>
                  <div className="text-muted mt-1" style={{ fontSize: '13px' }}>
                    <span className="fw-semibold text-dark">{ordenDesglose.paciente}</span> &bull; Total atenciones requeridas: <strong className="text-primary" style={{ fontSize: '14px' }}>{ordenDesglose.cantidad || 1}</strong>
                  </div>
                </div>
                <button type="button" className="btn-close" onClick={cerrarModalDesglose}></button>
              </div>

              <div className="modal-body p-4">
                <div style={{ backgroundColor: '#EFF6FF', color: '#1E40AF', border: '1px solid #BFDBFE', borderRadius: '10px', fontSize: '13px', lineHeight: 1.5, padding: '12px 16px', marginBottom: '20px' }}>
                  <i className="fa fa-info-circle me-1"></i>
                  Permite pagar una parte de las atenciones a una tarifa y el resto a otra tarifa (ejemplo: 8 atenciones a $18.000 y 2 a $15.000).
                </div>

                <div className="table-responsive">
                  <table className="table align-middle mb-3" style={{ fontSize: '13px' }}>
                    <thead style={{ background: '#F8FAFC', borderBottom: '2px solid #E2E8F0' }}>
                      <tr>
                        <th style={{ width: '130px', textAlign: 'center', padding: '10px 8px', color: '#1E293B', fontWeight: 700 }}>Cant.</th>
                        <th style={{ minWidth: '220px', textAlign: 'center', padding: '10px 8px', color: '#1E293B', fontWeight: 700 }}>Precio Unitario</th>
                        <th style={{ minWidth: '140px', textAlign: 'right', padding: '10px 8px', color: '#1E293B', fontWeight: 700 }}>Subtotal</th>
                        <th style={{ width: '50px', textAlign: 'center', padding: '10px 8px' }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {tramosDesglose.map((t, idx) => (
                        <tr key={idx} style={{ borderBottom: '1px solid #F1F5F9' }}>
                          <td style={{ padding: '8px' }}>
                            <input
                              type="number"
                              min="1"
                              max={ordenDesglose.cantidad || 1}
                              className="form-control text-center fw-bold"
                              style={{
                                height: '44px',
                                fontSize: '16px',
                                color: '#1E293B',
                                border: '1.5px solid #CBD5E1',
                                borderRadius: '8px'
                              }}
                              value={t.cantidad}
                              onChange={e => cambiarTramo(idx, 'cantidad', e.target.value)}
                            />
                          </td>
                          <td style={{ padding: '8px' }}>
                            <div className="input-group" style={{ width: '100%', minWidth: '180px' }}>
                              <span className="input-group-text fw-bold" style={{ backgroundColor: '#F1F5F9', color: '#475569', fontSize: '15px', padding: '0 14px' }}>$</span>
                              <input
                                type="number"
                                min="0"
                                step="500"
                                className="form-control fw-bold text-end"
                                style={{
                                  height: '44px',
                                  fontSize: '16px',
                                  color: '#1E293B',
                                  border: '1.5px solid #CBD5E1',
                                  minWidth: '140px'
                                }}
                                value={t.precio}
                                onChange={e => cambiarTramo(idx, 'precio', e.target.value)}
                              />
                            </div>
                          </td>
                          <td className="text-end fw-bold" style={{ color: '#0054A1', fontSize: '17px', padding: '8px 12px' }}>
                            {formatMoneda(t.subtotal)}
                          </td>
                          <td className="text-center" style={{ padding: '8px 4px' }}>
                            {tramosDesglose.length > 1 && (
                              <button
                                type="button"
                                className="btn btn-outline-danger"
                                style={{ width: '40px', height: '40px', padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', borderRadius: '8px' }}
                                onClick={() => eliminarTramo(idx)}
                                title="Eliminar este tramo"
                              >
                                <FontAwesomeIcon icon={faTrashAlt} />
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="d-flex justify-content-between align-items-center mt-3 mb-3 flex-wrap gap-2">
                  <button
                    type="button"
                    className="btn btn-outline-primary d-flex align-items-center gap-2"
                    style={{ padding: '8px 16px', fontSize: '13px', fontWeight: 600, borderRadius: '8px' }}
                    onClick={agregarTramo}
                  >
                    <FontAwesomeIcon icon={faPlus} />
                    <span>Agregar otro tramo</span>
                  </button>

                  <div style={{ fontSize: '13px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span className="text-muted">Atenciones distribuidas: </span>
                    <span className={`badge ${
                      tramosDesglose.reduce((acc, c) => acc + Number(c.cantidad || 0), 0) === Number(ordenDesglose.cantidad || 1)
                        ? 'bg-success'
                        : 'bg-danger'
                    }`} style={{ fontSize: '14px', padding: '6px 12px', borderRadius: '8px' }}>
                      {tramosDesglose.reduce((acc, c) => acc + Number(c.cantidad || 0), 0)} / {ordenDesglose.cantidad || 1}
                    </span>
                  </div>
                </div>

                <div className="p-3 rounded d-flex justify-content-between align-items-center" style={{ background: '#E8F1FB', border: '1.5px solid #BFDBFE', borderRadius: '12px' }}>
                  <span style={{ fontSize: '14px', fontWeight: 700, color: '#0054A1' }}>Subtotal Atención:</span>
                  <span style={{ fontSize: '20px', fontWeight: 800, color: '#0054A1' }}>
                    {formatMoneda(tramosDesglose.reduce((acc, c) => acc + Number(c.subtotal || 0), 0))}
                  </span>
                </div>
              </div>

              <div className="modal-footer d-flex justify-content-between" style={{ padding: '16px 24px', borderTop: '1px solid #ECEFF5', background: '#FAFAFA' }}>
                <button type="button" className="btn btn-outline-secondary" style={{ padding: '10px 20px', fontSize: '14px', borderRadius: '8px' }} onClick={cerrarModalDesglose}>
                  Cancelar
                </button>
                <button
                  type="button"
                  className="btn btn-primary d-flex align-items-center gap-2"
                  style={{ padding: '10px 24px', fontSize: '14px', fontWeight: 700, borderRadius: '8px' }}
                  disabled={
                    guardandoDesglose ||
                    tramosDesglose.reduce((acc, c) => acc + Number(c.cantidad || 0), 0) !== Number(ordenDesglose.cantidad || 1)
                  }
                  onClick={guardarDesglose}
                >
                  <FontAwesomeIcon icon={faCheck} />
                  <span>{guardandoDesglose ? 'Guardando...' : 'Guardar Desglose'}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════ */}
      {/* MODAL VER ORDEN DETALLE COMPLETO                          */}
      {/* ══════════════════════════════════════════════════════════ */}
      {showModalOrden && ordenSeleccionadaId && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.65)', zIndex: 1085 }} tabIndex="-1">
          <div className="modal-dialog modal-fullscreen modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  Orden #{ordenSeleccionadaId} {ordenSeleccionadaPaciente ? `— ${ordenSeleccionadaPaciente}` : ''}
                </h5>
                <button type="button" className="btn-close" onClick={cerrarModalOrden}></button>
              </div>
              <div className="modal-body">
                <OrdenesDetalle />
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={cerrarModalOrden}>
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* ══════════════════════════════════════════════════════════ */}
      {/* MODAL VER PLANILLA DE SEGURIDAD SOCIAL                    */}
      {/* ══════════════════════════════════════════════════════════ */}
      {modalPlanilla && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.75)', zIndex: 1090 }} tabIndex="-1">
          <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content" style={{ borderRadius: '16px', overflow: 'hidden', border: 'none', boxShadow: '0 16px 48px rgba(0,0,0,0.3)' }}>
              
              <div className="modal-header d-flex justify-content-between align-items-center" style={{ padding: '16px 22px', background: '#1A2340', color: '#ffffff' }}>
                <div className="d-flex align-items-center gap-2">
                  <FontAwesomeIcon icon={faFileAlt} style={{ fontSize: '18px', color: '#38BDF8' }} />
                  <div>
                    <h5 className="modal-title m-0" style={{ fontWeight: 800, fontSize: '16px', color: '#ffffff' }}>
                      Planilla de Seguridad Social — Cuenta #{modalPlanilla.cuentaId}
                    </h5>
                    {modalPlanilla.profesional && (
                      <small style={{ color: '#94A3B8', fontSize: '12px' }}>
                        Profesional: {modalPlanilla.profesional}
                      </small>
                    )}
                  </div>
                </div>

                <div className="d-flex align-items-center gap-2">
                  <a
                    href={modalPlanilla.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-sm btn-outline-light d-flex align-items-center gap-1"
                    style={{ fontSize: '12px', padding: '5px 12px', borderRadius: '8px' }}
                  >
                    <FontAwesomeIcon icon={faExternalLinkAlt} />
                    <span>Abrir en pestaña nueva</span>
                  </a>
                  <button
                    type="button"
                    className="btn-close btn-close-white"
                    onClick={() => setModalPlanilla(null)}
                  ></button>
                </div>
              </div>

              <div className="modal-body p-0" style={{ background: '#0F172A', minHeight: '65vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {modalPlanilla.esPdf ? (
                  <iframe
                    src={modalPlanilla.url}
                    title="Visor de Planilla de Seguridad Social"
                    style={{ width: '100%', height: '75vh', border: 'none' }}
                  />
                ) : (
                  <div className="p-3 text-center" style={{ width: '100%' }}>
                    <img
                      src={modalPlanilla.url}
                      alt="Planilla de Seguridad Social"
                      style={{
                        maxWidth: '100%',
                        maxHeight: '75vh',
                        objectFit: 'contain',
                        borderRadius: '8px',
                        boxShadow: '0 8px 32px rgba(0,0,0,0.5)'
                      }}
                    />
                  </div>
                )}
              </div>

              <div className="modal-footer d-flex justify-content-between" style={{ padding: '12px 20px', background: '#F8FAFC', borderTop: '1px solid #E2E8F0' }}>
                <button
                  type="button"
                  className="btn btn-outline-danger btn-sm d-flex align-items-center gap-1"
                  onClick={() => {
                    const idParaQuitar = modalPlanilla.cuentaId;
                    setModalPlanilla(null);
                    handleQuitarPlanilla(idParaQuitar);
                  }}
                >
                  <FontAwesomeIcon icon={faTrashAlt} />
                  <span>Quitar esta Planilla</span>
                </button>
                <button
                  type="button"
                  className="btn btn-secondary btn-sm px-4"
                  onClick={() => setModalPlanilla(null)}
                >
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ESTILOS PARA EL LED PARPADEANTE ROJO */}
      <style>{`
        .led-blinking-red {
          width: 9px;
          height: 9px;
          background-color: #EF4444;
          border-radius: 50%;
          display: inline-block;
          box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.7);
          animation: ledPulseRedFront 1.4s infinite cubic-bezier(0.66, 0, 0, 1);
        }

        @keyframes ledPulseRedFront {
          0% {
            transform: scale(0.95);
            box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.7);
          }
          70% {
            transform: scale(1.15);
            box-shadow: 0 0 0 7px rgba(239, 68, 68, 0);
          }
          100% {
            transform: scale(0.95);
            box-shadow: 0 0 0 0 rgba(239, 68, 68, 0);
          }
        }
      `}</style>
    </div>
  );
};

export default GestionCuentaCobro;
